import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Calendar, Clock, MapPin, Users, Target,
  Lightbulb, Presentation, Sparkles, ChevronUp, ChevronDown
} from 'lucide-react';
import type { CommunicationPRIsME } from '../data/comitePRIsMECommunicationData';
import ChartRenderer from './ChartRenderer';
import GuessRevealModal from './GuessRevealModal';
import { formatDateLocal } from '../utils/dateFormatter';

interface CommunicationPRIsMEPrimesModalProps {
  isOpen: boolean;
  onClose: () => void;
  communication: CommunicationPRIsME;
}

export default function CommunicationPRIsMEPrimesModal({ 
  isOpen, 
  onClose, 
  communication 
}: CommunicationPRIsMEPrimesModalProps) {
  const [activeSujet, setActiveSujet] = useState<string | null>(
    communication.ordreDuJour[0]?.id || null
  );
  const [activePoint, setActivePoint] = useState<string | null>(
    communication.ordreDuJour[0]?.points[0]?.id || null
  );
  const [isGuessModalOpen, setIsGuessModalOpen] = useState(false);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);

  const sujetActif = communication.ordreDuJour.find(s => s.id === activeSujet) || communication.ordreDuJour[0];

  const couleurNiveau = {
    Stratégique: 'from-purple-500 to-pink-500',
    Tactique: 'from-blue-500 to-cyan-500',
    Opérationnel: 'from-green-500 to-emerald-500'
  };

  const getStatutColor = (statut: string) => {
    switch (statut) {
      case 'Complété': return 'bg-green-100 text-green-700 border-green-300';
      case 'En cours': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Planifié': return 'bg-indigo-100 text-indigo-700 border-indigo-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <div className="bg-white w-full h-full overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-violet-600 text-white">
                <motion.div
                  animate={{ height: isHeaderExpanded ? 'auto' : 'auto' }}
                  className="overflow-hidden"
                >
                  <div className={`p-6 ${isHeaderExpanded ? '' : 'pb-3'}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Sparkles className="w-8 h-8" />
                          <h2 className="text-4xl font-bold">{communication.titre}</h2>
                        </div>
                        <AnimatePresence>
                          {isHeaderExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="flex items-center gap-6 text-violet-100 text-base overflow-hidden"
                            >
                              <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                <span>{formatDateLocal(String(communication.date), 'fr-CA', { 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric',
                                })}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5" />
                                <span>{communication.heure} • {communication.dureeMinutes} min</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <MapPin className="w-5 h-5" />
                                <span>{communication.lieu}</span>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                          className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                          title={isHeaderExpanded ? 'Réduire' : 'Agrandir'}
                        >
                          {isHeaderExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                        <button
                          onClick={onClose}
                          className="text-white hover:bg-white/20 rounded-full p-3 transition-colors"
                        >
                          <X className="w-8 h-8" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
                
                <AnimatePresence>
                  {isHeaderExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <div className="flex items-center gap-6 text-violet-100 text-base mb-6">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{formatDateLocal(String(communication.date), 'fr-CA', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                            })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{communication.heure} • {communication.dureeMinutes} min</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5" />
                            <span>{communication.lieu}</span>
                          </div>
                        </div>

                        {/* Objectif Principal */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 mb-6">
                          <p className="text-base font-semibold mb-2 flex items-center gap-2">
                            <Target className="w-5 h-5" />
                            Objectif Principal
                          </p>
                          <p className="text-white/90 text-lg leading-relaxed">{communication.objectifPrincipal}</p>
                        </div>

                        {/* Participants */}
                        <div className="flex items-center gap-4">
                          <Users className="w-6 h-6" />
                          <div className="flex gap-3 flex-wrap">
                            {communication.participants.map((p) => (
                              <div key={p.id} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                                <img src={p.avatar} alt={p.prenom} className="w-8 h-8 rounded-full border-2 border-white" />
                                <span className="text-base font-medium">{p.prenom} {p.nom}</span>
                                <span className="text-xs text-white/80">{p.role}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden flex">
                {/* Sidebar - Liste des sujets */}
                <div className="w-96 border-r border-gray-200 bg-gray-50 overflow-y-auto">
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Presentation className="w-6 h-6 text-violet-600" />
                      Ordre du Jour
                    </h3>
                    <div className="space-y-2">
                      {communication.ordreDuJour.map((sujet) => (
                        <button
                          key={sujet.id}
                          onClick={() => {
                            setActiveSujet(sujet.id);
                            setActivePoint(sujet.points[0]?.id || null);
                          }}
                          className={`w-full text-left p-4 rounded-xl transition-all ${
                            activeSujet === sujet.id
                              ? 'bg-violet-600 text-white shadow-lg'
                              : 'bg-white hover:bg-gray-100 text-gray-900 border border-gray-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className={`text-xl font-bold ${
                                activeSujet === sujet.id ? 'text-white' : 'text-violet-600'
                              }`}>
                                {sujet.numero}
                              </span>
                              <div className="flex-1">
                                <span className="font-semibold text-base block">{sujet.titre}</span>
                                <div className="flex items-center gap-2 mt-1">
                                  <img 
                                    src={sujet.presentateur.avatar} 
                                    alt={sujet.presentateur.prenom}
                                    className="w-5 h-5 rounded-full border border-gray-300"
                                  />
                                  <span className={`text-xs ${
                                    activeSujet === sujet.id ? 'text-white/80' : 'text-gray-500'
                                  }`}>
                                    {sujet.presentateur.prenom}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                              activeSujet === sujet.id
                                ? 'bg-white/20 text-white'
                                : getStatutColor(sujet.statut)
                            }`}>
                              {sujet.statut}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              activeSujet === sujet.id
                                ? 'bg-white/20 text-white'
                                : 'bg-gray-200 text-gray-700'
                            }`}>
                              {sujet.niveau}
                            </span>
                            <span className={`text-xs ${
                              activeSujet === sujet.id ? 'text-white/80' : 'text-gray-500'
                            }`}>
                              {sujet.dureeMinutes} min
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50">
                  {sujetActif && (
                    <div className="p-8">
                      {/* Header du sujet */}
                      <div className="bg-white rounded-xl p-8 shadow-md mb-8 border-l-4 border-violet-500">
                        <div className="flex items-start justify-between mb-6">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-3">
                              <span className="bg-violet-600 text-white w-16 h-16 rounded-xl flex items-center justify-center font-bold text-2xl shadow-lg">
                                {sujetActif.numero}
                              </span>
                              <div className="flex-1">
                                <h3 className="text-3xl font-bold text-gray-900 mb-2">{sujetActif.titre}</h3>
                                <div className="flex items-center gap-4">
                                  <div className="flex items-center gap-2">
                                    <img 
                                      src={sujetActif.presentateur.avatar} 
                                      alt={sujetActif.presentateur.prenom}
                                      className="w-8 h-8 rounded-full border-2 border-violet-200"
                                    />
                                    <span className="text-base font-semibold text-gray-700">
                                      {sujetActif.presentateur.prenom} {sujetActif.presentateur.nom}
                                    </span>
                                  </div>
                                  <span className="text-sm text-gray-600">{sujetActif.presentateur.role}</span>
                                </div>
                              </div>
                            </div>
                            <p className="text-gray-700 mb-6 text-lg leading-relaxed">{sujetActif.description}</p>
                            <div className="flex items-center gap-4">
                              <span className={`px-4 py-2 rounded-full text-base font-semibold bg-gradient-to-r ${couleurNiveau[sujetActif.niveau] || 'from-gray-500 to-gray-600'} text-white`}>
                                {sujetActif.niveau}
                              </span>
                              <span className="text-base text-gray-600">
                                Durée: <strong className="text-gray-900">{sujetActif.dureeMinutes} min</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Points de présentation */}
                      <div className="space-y-6">
                        <h4 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Lightbulb className="w-6 h-6 text-violet-600" />
                          Points de Présentation
                        </h4>
                        {sujetActif.points
                          .filter(p => p.contenu !== '__DEVINETTE__')
                          .map((point, idx) => (
                          <motion.div
                            key={point.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className={`bg-white rounded-xl p-8 shadow-lg border-l-4 ${
                              point.id === activePoint ? 'border-violet-500 ring-2 ring-violet-200' : 'border-gray-300'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3">
                                  <span className="bg-violet-100 text-violet-700 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg">
                                    {idx + 1}
                                  </span>
                                  <h5 className="text-2xl font-bold text-gray-900">{point.titre}</h5>
                                </div>
                                <div className="text-gray-700 leading-relaxed text-base space-y-4 ml-13">
                                  {(() => {
                                    // Parser le contenu complet pour détecter les placeholders de graphiques
                                    // Les placeholders peuvent être sur plusieurs lignes
                                    const fullContent = point.contenu;
                                    
                                    // Regex améliorée pour capturer les placeholders même sur plusieurs lignes
                                    // Format: {{CHART:TYPE:SUBTYPE:{...}}}
                                    // On doit gérer les accolades imbriquées, donc on utilise une fonction de parsing personnalisée
                                    const findChartPlaceholders = (text: string): Array<{match: string, index: number}> => {
                                      const results: Array<{match: string, index: number}> = [];
                                      const startPattern = /\{\{CHART:(\w+):(\w+):/g;
                                      let match;
                                      
                                      while ((match = startPattern.exec(text)) !== null) {
                                        const startIndex = match.index;
                                        const jsonStart = match.index + match[0].length;
                                        
                                        // Compter les accolades pour trouver la fin du JSON
                                        let braceCount = 0;
                                        let jsonEnd = jsonStart;
                                        let inString = false;
                                        let escapeNext = false;
                                        
                                        for (let i = jsonStart; i < text.length - 1; i++) {
                                          const char = text[i];
                                          
                                          if (escapeNext) {
                                            escapeNext = false;
                                            continue;
                                          }
                                          
                                          if (char === '\\') {
                                            escapeNext = true;
                                            continue;
                                          }
                                          
                                          if (char === '"' && !escapeNext) {
                                            inString = !inString;
                                            continue;
                                          }
                                          
                                          if (!inString) {
                                            if (char === '{') {
                                              braceCount++;
                                            } else if (char === '}') {
                                              braceCount--;
                                              if (braceCount === 0) {
                                                jsonEnd = i + 1;
                                                // Vérifier que c'est bien suivi de }}
                                                if (text.substring(jsonEnd, jsonEnd + 2) === '}}') {
                                                  const fullMatch = text.substring(startIndex, jsonEnd + 2);
                                                  results.push({ match: fullMatch, index: startIndex });
                                                }
                                                break;
                                              }
                                            }
                                          }
                                        }
                                      }
                                      
                                      return results;
                                    };
                                    
                                    const chartMatches = findChartPlaceholders(fullContent);
                                    
                                    if (chartMatches.length === 0) {
                                      // Pas de graphiques, traitement normal
                                      return fullContent.split('\n\n').map((section, sIdx) => {
                                        const lines = section.split('\n');
                                        const title = lines[0];
                                        const content = lines.slice(1);
                                        
                                        const isTitle = title.includes('**') && title.includes(':') && !title.startsWith('•');
                                        
                                        return (
                                          <div key={sIdx} className="space-y-3">
                                            {isTitle && content.length > 0 ? (
                                              <>
                                                <h6 className="font-semibold text-gray-900 text-lg mb-2 border-b border-gray-200 pb-2">{title.replace(/\*\*/g, '')}</h6>
                                                <ul className="space-y-2.5 ml-2">
                                                  {content.map((line, lIdx) => (
                                                    line.trim() && (
                                                      <li key={lIdx} className="flex items-start gap-2.5">
                                                        <span className="text-violet-600 mt-1.5 flex-shrink-0">•</span>
                                                        <span className="flex-1 leading-relaxed">{line.replace(/^•\s*/, '')}</span>
                                                      </li>
                                                    )
                                                  ))}
                                                </ul>
                                              </>
                                            ) : (
                                              <p className="leading-relaxed">{section}</p>
                                            )}
                                          </div>
                                        );
                                      });
                                    }
                                    
                                    // Construire le résultat avec texte et graphiques
                                    const result: React.ReactNode[] = [];
                                    let lastIndex = 0;
                                    
                                    chartMatches.forEach((chartMatch, mIdx) => {
                                      const match = { 0: chartMatch.match, index: chartMatch.index };
                                      // Ajouter le texte avant le placeholder
                                      if (match.index !== undefined && match.index > lastIndex) {
                                        const textBefore = fullContent.substring(lastIndex, match.index);
                                        if (textBefore.trim()) {
                                          // Traiter le texte par sections
                                          textBefore.split('\n\n').forEach((section, sIdx) => {
                                            if (section.trim()) {
                                              const lines = section.split('\n');
                                              const title = lines[0];
                                              const content = lines.slice(1);
                                              
                                              const isTitle = title.includes('**') && title.includes(':') && !title.startsWith('•');
                                              
                                              if (isTitle && content.length > 0) {
                                                const cleanTitle = title.replace(/\*\*/g, '');
                                                result.push(
                                                  <div key={`text-${mIdx}-${sIdx}`} className="space-y-3">
                                                    <h6 className="font-semibold text-gray-900 text-lg mb-2 border-b border-gray-200 pb-2">{cleanTitle}</h6>
                                                    <ul className="space-y-2.5 ml-2">
                                                      {content.map((line, lIdx) => (
                                                        line.trim() && (
                                                          <li key={lIdx} className="flex items-start gap-2.5">
                                                            <span className="text-violet-600 mt-1.5 flex-shrink-0">•</span>
                                                            <span className="flex-1 leading-relaxed">{line.replace(/^•\s*/, '')}</span>
                                                          </li>
                                                        )
                                                      ))}
                                                    </ul>
                                                  </div>
                                                );
                                              } else if (section.trim()) {
                                                result.push(
                                                  <p key={`text-${mIdx}-${sIdx}`} className="leading-relaxed">{section}</p>
                                                );
                                              }
                                            }
                                          });
                                        }
                                      }
                                      
                                      // Ajouter le graphique
                                      result.push(
                                        <div key={`chart-${mIdx}`} className="my-6">
                                          <ChartRenderer placeholder={match[0]} />
                                        </div>
                                      );
                                      
                                      lastIndex = (match.index || 0) + match[0].length;
                                    });
                                    
                                    // Ajouter le texte après le dernier placeholder
                                    if (lastIndex < fullContent.length) {
                                      const textAfter = fullContent.substring(lastIndex);
                                      if (textAfter.trim()) {
                                        textAfter.split('\n\n').forEach((section, sIdx) => {
                                          if (section.trim()) {
                                            const lines = section.split('\n');
                                            const title = lines[0];
                                            const content = lines.slice(1);
                                            
                                            const isTitle = title.includes('**') && title.includes(':') && !title.startsWith('•');
                                            
                                            if (isTitle && content.length > 0) {
                                              const cleanTitle = title.replace(/\*\*/g, '');
                                              result.push(
                                                <div key={`text-after-${sIdx}`} className="space-y-3">
                                                  <h6 className="font-semibold text-gray-900 text-lg mb-2 border-b border-gray-200 pb-2">{cleanTitle}</h6>
                                                  <ul className="space-y-2.5 ml-2">
                                                    {content.map((line, lIdx) => (
                                                      line.trim() && (
                                                        <li key={lIdx} className="flex items-start gap-2.5">
                                                          <span className="text-violet-600 mt-1.5 flex-shrink-0">•</span>
                                                          <span className="flex-1 leading-relaxed">{line.replace(/^•\s*/, '')}</span>
                                                        </li>
                                                      )
                                                    ))}
                                                  </ul>
                                                </div>
                                              );
                                            } else {
                                              result.push(
                                                <p key={`text-after-${sIdx}`} className="leading-relaxed">{section}</p>
                                              );
                                            }
                                          }
                                        });
                                      }
                                    }
                                    
                                    return result;
                                  })()}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 ml-4">
                                <span className="text-xs px-3 py-1 rounded-full font-semibold bg-violet-100 text-violet-700">
                                  {point.dureeMinutes} min
                                </span>
                              </div>
                            </div>

                            {/* Graphiques et Concepts */}
                            {(point.graphiques && point.graphiques.length > 0) || (point.concepts && point.concepts.length > 0) ? (
                              <div className="mt-6 pt-6 border-t border-gray-200">
                                {point.graphiques && point.graphiques.length > 0 && (
                                  <div className="mb-4">
                                    <span className="text-sm font-semibold text-gray-700 mb-2 block">Graphiques suggérés:</span>
                                    <div className="flex flex-wrap gap-2">
                                      {point.graphiques.map((graph, gIdx) => (
                                        <span key={gIdx} className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                          {graph}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {point.concepts && point.concepts.length > 0 && (
                                  <div>
                                    <span className="text-sm font-semibold text-gray-700 mb-2 block">Concepts clés:</span>
                                    <div className="flex flex-wrap gap-2">
                                      {point.concepts.map((concept, cIdx) => (
                                        <span key={cIdx} className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                                          {concept}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </motion.div>
                        ))}
                      </div>
                      {sujetActif.points.some(p => p.contenu === '__DEVINETTE__') && (
                        <motion.div
                          key="devinette"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-violet-500 mt-6"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1 space-y-3 text-gray-700">
                              <div className="flex items-center gap-3 mb-3">
                                <span className="bg-violet-100 text-violet-700 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg">
                                  1
                                </span>
                                <h5 className="text-2xl font-bold text-gray-900">Devine le temps de création</h5>
                              </div>
                              <p>Devine en combien de temps cette communication a été bâtie. Clique pour lancer le suspense (15s) puis révéler la réponse.</p>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => { setIsGuessModalOpen(true); }}
                                  className="px-10 py-5 text-2xl uppercase bg-violet-600 text-white font-black rounded-2xl shadow-xl hover:bg-violet-700 transition"
                                >
                                  Deviner
                                </button>
                              </div>
                              <div className="text-sm text-gray-600">
                                Gagnant : carte cadeau 500$. Deuxième : carte cadeau 250$.
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
          <GuessRevealModal isOpen={isGuessModalOpen} onClose={() => setIsGuessModalOpen(false)} />
        </>
      )}
    </AnimatePresence>
  );
}

